import React, {useEffect, useState} from 'react';
import './ProjectsReports.scss';
import ProjectsHero from '../../Components/Projects/ProjectsHero';
import {ReactComponent as AddPhotoSvg} from '../../../assets/icons/addPhoto.svg';
import {ReactComponent as AddVideoSvg} from '../../../assets/icons/addVideo.svg';
import {ReactComponent as Gallery} from '../../../assets/icons/gallery.svg';
import {ReactComponent as GalleryVideo} from '../../../assets/icons/videoGallery.svg';
import {IconButtonMUI, TabItem, Tabs} from '../../../shared';
import ProjectReportsItem from './ProjectReportsItem';
import Dropzone from '../../Components/Dropzone';
import ButtonMUI from '../../../shared/ButtonMUI';
import {useDispatch, useSelector} from 'react-redux';
import {
  deleteReport,
  downloadReport,
  getReportsPhoto,
  getReportsVideo,
  uploadReport
} from '../../../redux/actions/reports/reportsActions';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import LoadPanel from '../../Components/LoadPanel';
import NoItems from '../../Components/NoItems';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {notifySuccess} from '../../../helpers/notifySnack';
import {getProject} from '../../../redux/actions/projects/projectsActions';
import {hasPermission} from '../../../helpers/functions';
import {PERMISSIONS} from '../../../helpers/constants';
import {useTranslation} from 'react-i18next';
import {closeModal, setModal, showConfirmPopup} from '../../../redux/actions/app/appActions';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import useActivityControlledTimer from '../../../helpers/hooks';

const ProjectsReports = () => {
  const history = useHistory();
  const location = useLocation();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {singleProject} = useSelector((state) => state.projects);
  const {reportsVideo, reportsPhoto, reportsVideoLoading, reportsPhotoLoading} = useSelector(({reports}) => reports);
  const [defaultIndex, setDefaultIndex] = useState('1');
  const {id} = useParams();
  const {profileMe} = useSelector((state) => state.profile);

  const validationSchema = yup.object().shape({
    items: yup.array().min(1, 'At least one photo is required').nullable()
  });
  const CHUNK_SIZE = 150 * 1024 * 1024;
  const {
    handleSubmit,
    setValue,
    formState: {isValid, isSubmitting}
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      items: []
    }
  });

  const [showDownloadPage, setShowDownloadPage] = useState({
    visible: false,
    type: ''
  });

  const [showTabsDesktop, setShowTabsDesktop] = useState(window.innerWidth > 625);
  const [showGalleryItems, setShowGalleryItems] = useState('');

  const handleResize = () => {
    if (window.innerWidth > 625) {
      setShowTabsDesktop(true);
    } else {
      setShowTabsDesktop(false);
    }
  };


  const uploadPhotoHandler = () => {
    setValue('items', [], {shouldValidate: true});
    setShowDownloadPage({
      visible: true,
      type: 'image'
    });
  };

  const uploadVideoHandler = () => {
    setValue('items', [], {shouldValidate: true});
    setShowDownloadPage({
      visible: true,
      type: 'video'
    });
  };

  const [checkedItems, setCheckedItems] = useState({
    photos: [],
    videos: []
  });

  const [isCheckAll, setIsCheckAll] = useState({
    photos: false,
    videos: false
  });

  const handleCheckAll = (type) => {
    const itemsToCheck =
      type === 'photos' ? reportsPhoto.flatMap((item) => item.id) : reportsVideo.flatMap((item) => item.id);

    setCheckedItems((prev) => ({
      ...prev,
      [type]: isCheckAll[type] ? [] : itemsToCheck
    }));

    setIsCheckAll((prev) => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleCheck = (type, id) => {
    setCheckedItems((prev) => ({
      ...prev,
      [type]: prev[type].includes(id) ? prev[type].filter((item) => item !== id) : [...prev[type], id]
    }));
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onFilesAdded = (files) => {
    setValue('items', files, {shouldValidate: true});
  };

  const handleNavigate = () => {
    setShowDownloadPage({
      visible: false,
      type: ''
    });
  };

  useEffect(() => {
    if (id) {
      dispatch(getReportsPhoto(id));
      dispatch(getReportsVideo(id));
    }
    return () => {
      setShowDownloadPage({
        visible: false,
        type: ''
      });
    }
  }, [id]);

  const onAddedItem = async (data) => {
    if (data.items && data.items.length) {
      const maxChunkSize = CHUNK_SIZE; // Максимальный размер чанка (например, 50 МБ)
      const maxFilesPerChunk = 15; // Максимальное количество файлов в чанке
      let chunkFiles = [];
      let currentChunkSize = 0;

      for (let idx = 0; idx < data.items.length; idx++) {
        const item = data.items[idx];
        const itemSize = item.size;

        if (currentChunkSize + itemSize <= maxChunkSize && chunkFiles.length < maxFilesPerChunk) {
          chunkFiles.push(item);
          currentChunkSize += itemSize;
        } else {
          if (chunkFiles.length > 0) {
            const chunkFormData = new FormData();
            chunkFormData.append('type', showDownloadPage.type);
            chunkFormData.append('name', new Date().getTime());

            chunkFiles.forEach((file, i) => {
              chunkFormData.append(`file[${i}]`, file);
            });

            const res = await dispatch(uploadReport(id, chunkFormData));
            if (res.payload) {
              console.log('Chunk uploaded successfully');
            } else {
              console.error('Failed to upload chunk');
              return;
            }
          }

          chunkFiles = [item];
          currentChunkSize = itemSize;
        }
      }

      if (chunkFiles.length > 0) {
        const chunkFormData = new FormData();
        chunkFormData.append('type', showDownloadPage.type);
        chunkFormData.append('name', new Date().getTime());

        chunkFiles.forEach((file, i) => {
          chunkFormData.append(`file[${i}]`, file);
        });

        const res = await dispatch(uploadReport(id, chunkFormData));
        if (res.payload) {
          console.log('Last chunk uploaded successfully');
        } else {
          console.error('Failed to upload last chunk');
          return;
        }
      }
    }

    handleNavigate();
    await dispatch(getProject(id,true));
    // await dispatch(getReportsPhoto(id));
    // await dispatch(getReportsVideo(id));
  };
  const deleteReportHandler = async (ids, type) => {
    try {
      const res = await dispatch(
        deleteReport(id, {
          ids: [ids]
        })
      );

      if (res.payload) {
        await dispatch(getProject(id));
        type === 'photos' ? await dispatch(getReportsPhoto(id)) : await dispatch(getReportsVideo(id));
        notifySuccess(t('all.successfully'));
      }
    } catch (err) {}
  };

  const deleteReportMultiple = async (type) => {
    try {
      const res = await dispatch(
        deleteReport(id, {
          ids: type === 'photos' ? checkedItems.photos : checkedItems.videos
        })
      );
      if (res.payload) {
        await dispatch(getProject(id));
        type === 'photos' ? await dispatch(getReportsPhoto(id)) : await dispatch(getReportsVideo(id));
        notifySuccess(t('all.successfully'));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onTabClick = (type) => {
    const params = new URLSearchParams(window.location.search);
    params.set('type', type);
    history.push({
      pathname: window.location.pathname,
      search: params.toString()
    });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('type') === '2') {
      setDefaultIndex('2');
    }
  }, []);

  const deleteReportMultipleReduxHandler = (type) => {
    const checkedType = type === 'photos' ? checkedItems.photos : checkedItems.videos;
    if (checkedType.length) {
      dispatch(
        showConfirmPopup({
          visible: true,
          confirmCallback: () => deleteReportMultiple(type)
        })
      );
    }
  };

  const deleteReportSingleReduxHandler = (ids, type) => {
    dispatch(
      showConfirmPopup({
        visible: true,
        confirmCallback: () => deleteReportHandler(ids, type)
      })
    );
  };

  const permissionCreate = hasPermission(PERMISSIONS.CREATE_REPORTS, profileMe?.permissions ?? []);

  const allowActions = singleProject?.status === 'active' || singleProject?.status === 'pause'

  const reportsDownload = async (type) => {
    const checkedType = type === 'photos' ? checkedItems.photos : checkedItems.videos;
    if (checkedType.length) {
      dispatch(setModal(POPUPS.PREPARING_POPUP));
      dispatch(downloadReport(checkedType)).then((res) => {
        if (res.payload) {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(res.payload.data);
          link.download = 'items.zip';
          document.body.appendChild(link);

          link.click();

          link.parentNode.removeChild(link);
        }
        dispatch(closeModal());
      });
    }
  };

  useActivityControlledTimer(id);

  return (
    <div className={'projects__gallery'}>
      {reportsVideoLoading || reportsPhotoLoading ? (
        <LoadPanel noBg={true} circleSize={50} staticPos={true} />
      ) : (
        <>
          <ProjectsHero
            buttonHandler={handleNavigate}
            showArrow={showDownloadPage.visible}
            heading={t('ProjectsReports.reports.heading')}
            subHeading={t('ProjectsReports.reports.subHeading')}
            buttons={
              <>
                {permissionCreate && allowActions ? (
                  <>
                    <IconButtonMUI
                      color={'default'}
                      onClick={uploadPhotoHandler}
                      title={t('ProjectsReports.reports.addPhoto')}
                    >
                      <AddPhotoSvg />
                    </IconButtonMUI>
                    <IconButtonMUI
                      color={'default'}
                      onClick={uploadVideoHandler}
                      title={t('ProjectsReports.reports.addVideo')}
                    >
                      <AddVideoSvg />
                    </IconButtonMUI>
                  </>
                ) : null}
              </>
            }
          />
          {!showDownloadPage.visible && (
            <div className='projects__gallery-inner'>
              {showTabsDesktop && (
                <Tabs
                  className={'projects__gallery-tabs'}
                  defaultIndex={'1'}
                  changeTabIndex={defaultIndex}
                  onTabClick={onTabClick}
                >
                  <TabItem
                    label={t('ProjectsReports.tabs.photoReport')}
                    icon={<Gallery />}
                    count={reportsPhoto.length > 0 ? reportsPhoto.length : null}
                    index='1'
                  >
                    {reportsPhoto.length ? (
                      <ProjectReportsItem
                        handleCheckAll={handleCheckAll}
                        checkedItems={checkedItems}
                        isCheckAll={isCheckAll}
                        handleCheck={handleCheck}
                        array={reportsPhoto}
                        deleteReportMultiple={deleteReportMultipleReduxHandler}
                        deleteReportHandler={deleteReportSingleReduxHandler}
                        reportsDownload={reportsDownload}
                        type={'photos'}
                      />
                    ) : (
                      <div className={'projects__gallery-noItems'}>
                        <NoItems
                          description={t('ProjectsReports.reports.noReports.description')}
                          title={t('ProjectsReports.reports.noReports.title')}
                          handleNavigate={uploadPhotoHandler}
                          buttonText={t('ProjectsReports.reports.noReports.buttonText')}
                          center={'center'}
                          disabled={!permissionCreate || !allowActions}
                        />
                      </div>
                    )}
                  </TabItem>
                  <TabItem
                    label={t('ProjectsReports.tabs.videoReport')}
                    icon={<GalleryVideo />}
                    count={reportsVideo?.length ? reportsVideo.length : null}
                    index='2'
                  >
                    {reportsVideo.length ? (
                      <ProjectReportsItem
                        handleCheckAll={handleCheckAll}
                        checkedItems={checkedItems}
                        isCheckAll={isCheckAll}
                        handleCheck={handleCheck}
                        array={reportsVideo}
                        deleteReportHandler={deleteReportSingleReduxHandler}
                        deleteReportMultiple={deleteReportMultipleReduxHandler}
                        reportsDownload={reportsDownload}
                        type={'videos'}
                      />
                    ) : (
                      <div className={'projects__gallery-noItems'}>
                        <NoItems
                          description={t('ProjectsReports.reports.noVideoReports.description')}
                          title={t('ProjectsReports.reports.noVideoReports.title')}
                          handleNavigate={uploadVideoHandler}
                          buttonText={t('ProjectsReports.reports.noVideoReports.buttonText')}
                          center={'center'}
                          disabled={!permissionCreate || !allowActions}
                        />
                      </div>
                    )}
                  </TabItem>
                </Tabs>
              )}

              {/*mobile*/}
              {!showTabsDesktop && !showGalleryItems && (
                <div className={'projects__gallery-blocks'}>
                  <div
                    className='projects__gallery-block'
                    onClick={() => {
                      setShowGalleryItems('photos');
                    }}
                  >
                    <Gallery />
                    <span>{t('ProjectsReports.tabs.photoReport')}</span>
                    <div className='count'>{reportsPhoto?.length ?? 0}</div>
                  </div>
                  <div
                    className='projects__gallery-block'
                    onClick={() => {
                      setShowGalleryItems('videos');
                    }}
                  >
                    <GalleryVideo />
                    <span>{t('ProjectsReports.tabs.videoReport')}</span>
                    <div className='count'>{reportsVideo?.length ?? 0}</div>
                  </div>
                </div>
              )}

              {showGalleryItems === 'photos' && (
                <ProjectReportsItem
                  handleCheckAll={handleCheckAll}
                  checkedItems={checkedItems}
                  isCheckAll={isCheckAll}
                  handleCheck={handleCheck}
                  array={reportsPhoto}
                  type={'photos'}
                  deleteReportHandler={deleteReportSingleReduxHandler}
                  deleteReportMultiple={deleteReportMultipleReduxHandler}
                  reportsDownload={reportsDownload}
                />
              )}

              {showGalleryItems === 'videos' && (
                <ProjectReportsItem
                  handleCheckAll={handleCheckAll}
                  checkedItems={checkedItems}
                  isCheckAll={isCheckAll}
                  handleCheck={handleCheck}
                  array={reportsVideo}
                  type={'videos'}
                  deleteReportMultiple={deleteReportMultipleReduxHandler}
                  deleteReportHandler={deleteReportSingleReduxHandler}
                  reportsDownload={reportsDownload}
                />
              )}
            </div>
          )}

          {/*downloadPage*/}
          {showDownloadPage.visible && (
            <div className={'projects__gallery-download'}>
              <div className='projects__gallery-content'>
                <h3>
                  {t('ProjectsReports.common.Download')}{' '}
                  {showDownloadPage.type === 'image'
                    ? t('ProjectsReports.tabs.photoReport')
                    : t('ProjectsReports.tabs.videoReport')}
                </h3>
                <Dropzone
                  maxFiles={50}
                  onFilesAdded={onFilesAdded}
                  maxSizeMb={100}
                  disabled={isSubmitting}
                  allowFormats={
                    showDownloadPage.type === 'image' ? ['.jpg', '.png', '.jpeg'] : ['.mp4', '.avi', '.mov']
                  }
                />
                <div className='projects__gallery-submit'>
                  <ButtonMUI
                    fullWidth={true}
                    formAction
                    loading={isSubmitting}
                    disabled={!isValid || isSubmitting}
                    onClick={handleSubmit(onAddedItem)}
                  >
                    {t('ProjectsReports.common.Download')}
                  </ButtonMUI>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
export default ProjectsReports;
